import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import TopBar from '../layout/TopBar';
import StatusBadge from '../components/StatusBadge';
import { useHGS } from '../context/HGSContext';
import { ArchitectureProgress } from './RiskUniversePage';
import { Wrench, AlertTriangle, CheckCircle2, Clock, Filter, ArrowRight, FlameKindling, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/utils';

const MODULE_COLORS = {
  FRAUD: 'bg-sky-100 text-sky-800 border-sky-200',
  PCC: 'bg-violet-100 text-violet-800 border-violet-200',
  DLC: 'bg-amber-100 text-amber-800 border-amber-200',
};

export default function CountermeasuresPage() {
  const { countermeasures, closeCountermeasure, user } = useHGS();
  const [statusFilter, setStatusFilter] = useState('All');
  const [moduleFilter, setModuleFilter] = useState('All');

  // RBAC: Department Heads only see their dept's countermeasures
  const scoped = useMemo(() => {
    if (user?.role !== 'department_head') return countermeasures;
    return countermeasures.filter((c) => (c.department || '').toLowerCase() === (user.department || '').toLowerCase());
  }, [countermeasures, user]);

  const filtered = useMemo(() => scoped.filter((c) =>
    (statusFilter === 'All' || c.status === statusFilter) &&
    (moduleFilter === 'All' || c.module === moduleFilter)
  ), [scoped, statusFilter, moduleFilter]);

  const stats = useMemo(() => {
    const open = scoped.filter((c) => c.status !== 'Closed');
    const overdue = open.filter((c) => c.daysOverdue > 0);
    const escalated = open.filter((c) => c.escalated);
    return { total: scoped.length, open: open.length, overdue: overdue.length, escalated: escalated.length };
  }, [scoped]);

  const empty = filtered.length === 0;

  return (
    <div>
      <TopBar
        title="Countermeasures"
        subtitle="Auto-generated from every No/NG response across Fraud, PCC and DLC · escalated to Compliance Officer after 30 days"
        breadcrumbs={['HGS Governance', 'Countermeasures']}
      />

      <div className="p-8 space-y-5">
        <ArchitectureProgress step={4} />

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total" value={stats.total} icon={Wrench} tone="slate" />
          <Stat label="Open" value={stats.open} icon={Clock} tone="amber" />
          <Stat label="Overdue" value={stats.overdue} icon={AlertTriangle} tone="red" />
          <Stat label="Escalated" value={stats.escalated} icon={FlameKindling} tone="red" testId="stat-escalated" />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            <select data-testid="filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white">
              {['All', 'Open', 'Closed'].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <select data-testid="filter-module" value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)} className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white">
            {['All', 'FRAUD', 'PCC', 'DLC'].map((m) => <option key={m}>{m}</option>)}
          </select>
          <span className="text-[11px] text-slate-500">Showing {filtered.length} of {scoped.length}{user?.role === 'department_head' ? ` · scoped to ${user.department}` : ''}</span>
        </div>

        {/* List */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {empty ? (
            <div className="p-12 text-center" data-testid="countermeasures-empty">
              <ShieldAlert className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <p className="text-sm font-bold text-slate-700">No countermeasures yet</p>
              <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">Answer any Fraud, PCC or DLC question with "No" or "NG" and a countermeasure task will appear here automatically.</p>
              <Link to="/fraud" className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-[#CC0000] hover:underline">
                Go to Fraud Prevention <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map((c) => (
                <div key={c.id} className="p-4 hover:bg-slate-50 transition flex items-start gap-4" data-testid={`cm-row-${c.id}`}>
                  <div className={cn('px-2 py-1 rounded border text-[10px] font-bold uppercase flex-shrink-0', MODULE_COLORS[c.module] || 'bg-slate-100 text-slate-700 border-slate-200')}>
                    {c.module}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono text-[11px] font-bold text-slate-700">{c.id}</span>
                      <span className="text-slate-300">·</span>
                      <span className="text-xs text-slate-500">Linked to <span className="font-mono font-bold text-slate-700">{c.relatedId}</span></span>
                      {c.riskId && <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 font-bold">{c.riskId}</span>}
                      {c.escalated && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-600 text-white text-[9px] font-bold uppercase">
                          <FlameKindling className="w-2.5 h-2.5" />
                          Escalated to Compliance Officer
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-slate-900">{c.issue}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
                      <span>Owner: <span className="font-semibold text-slate-700">{c.owner}</span></span>
                      <span>·</span>
                      <span>Due: <span className="font-semibold text-slate-700">{c.dueDate}</span></span>
                      {c.daysOverdue > 0 && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-100 text-red-800 text-[10px] font-bold">
                          {c.daysOverdue}d overdue
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-2">
                    <StatusBadge status={c.status === 'Open' ? 'Pending Review' : 'Approved'} />
                    {c.status === 'Open' && (
                      <button
                        data-testid={`close-cm-${c.id}`}
                        onClick={() => closeCountermeasure(c.id, 'Resolved')}
                        className="px-2.5 py-1 rounded-md text-[11px] font-bold text-emerald-700 hover:bg-emerald-50 border border-emerald-200"
                      >
                        <CheckCircle2 className="w-3 h-3 inline mr-1" />
                        Close
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone = 'slate', testId }) {
  const tones = {
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    red: 'bg-red-50 border-red-200 text-red-800',
  };
  return (
    <div className={cn('rounded-xl border p-4', tones[tone])} data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5 opacity-60" />}
      </div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}
